import { TextProps as FluentTextProps } from '@fluentui/react-components';

import { ComponentWithLegacyClassNameProps } from '../../util/ComponentWithLegacyClassName/props';
import { PaletteColor, SemanticPaletteColor } from '../../util/colors';
import { Display } from '../../util/styles/display';
import { TypographyPreset, Weight } from '../../util/styles/fluent9';

export type { PaletteColor, Weight, TypographyPreset };

export enum TextColor {
  /**
   * Primary black text color
   */
  Primary = 'primary',

  /**
   * Secondary dark gray text color
   */
  Secondary = 'secondary',

  /**
   * Accent color for mentions, topics, etc.
   */
  Accent = 'accent',

  /**
   * Tertiary color for metadata
   */
  Metadata = 'metadata',

  /*
   * For use on darker backgrounds.
   */
  White = 'white',

  /**
   * For use on darker backgrounds. Note: This will always be white, even in dark mode.
   */
  StaticWhite = 'staticWhite',

  /**
   * For use in error messages.
   */
  Error = 'error',

  /**
   * For use in interaction elements which have become disabled.
   */
  Disabled = 'disabled',
}

export interface TextProps
  extends ComponentWithLegacyClassNameProps,
    Pick<FluentTextProps, 'as' | 'font' | 'italic' | 'strikethrough' | 'underline' | 'weight'> {
  /**
   * The amount of background alpha, a number between 0 and 100. Defaults to 100.
   */
  readonly backgroundAlpha?: number;

  /**
   * Background color.
   */
  readonly backgroundColor?: SemanticPaletteColor;

  /**
   * Text color
   */
  readonly color?: TextColor;

  /**
   * Sets dir attribute on element. Use 'auto' for user-entered content where the language
   * isn't known (eg user and group names), or ltr/rtl where language is known (eg message
   * titles, bodies). Note that ltr/rtl do not change text alignment, only the layout
   * direction within the span.
   */
  readonly dir?: 'auto' | 'ltr' | 'rtl';

  /**
   * Sets the display property.
   * @defaultValue 'inline'
   */
  readonly display?: Display;

  /**
   * If provided, sets the lang= attribute to specify the language of the content
   */
  readonly language?: string;

  /**
   * Defines the font-size and line-height for this text.
   * Prefer `preset` for more consistent sizes and weights.
   */
  readonly size?: FluentTextProps['size'];

  /**
   * Fluent typography presets which override font, weight, size props.
   * Preferred to use this prop instead of `size`.
   */
  readonly preset?: TypographyPreset;

  /**
   * If provided, will hide child content from screenreaders using aria-hidden while making
   * the given screenreaderText available to screenreaders.
   */
  readonly screenreaderText?: string;

  /**
   * Sets text-transform: uppercase.
   */
  readonly uppercase?: boolean;
}
